// src/components/user/controltower/ControlTower.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Crosshair, Info, Layers, MapPin, Phone, X } from 'lucide-react';
import { useControlTower } from './useControlTower';
import { TowerHeader } from './TowerHeader';
import { TowerSidebar } from './TowerSidebar';
import RegularDeliveryModal from './RegularDeliveryModal';
import StatusFooter from './StatusFooter';
import { getAlertLabel } from '../../delivery-alerts/alertTypes';
import StoreDeliveryIntelligenceDrawer from '../intelligence/StoreDeliveryIntelligenceDrawer';

const DISTRICTS = {
  Ancon: { latitude: -11.770, longitude: -77.155 },
  Ate: { latitude: -12.037, longitude: -76.955 },
  Barranco: { latitude: -12.145, longitude: -77.021 },
  'Brena': { latitude: -12.063, longitude: -77.049 },
  Carabayllo: { latitude: -11.884, longitude: -77.038 },
  Chorrillos: { latitude: -12.178, longitude: -77.001 },
  Chosica: { latitude: -11.937, longitude: -76.696 },
  Comas: { latitude: -11.936, longitude: -77.060 },
  'El Agustino': { latitude: -12.047, longitude: -76.997 },
  Huaycan: { latitude: -12.016, longitude: -76.831 },
  Independencia: { latitude: -11.982, longitude: -77.049 },
  'Jesus Maria': { latitude: -12.076, longitude: -77.049 },
  'La Molina': { latitude: -12.073, longitude: -76.947 },
  'La Victoria': { latitude: -12.071, longitude: -77.013 },
  Lince: { latitude: -12.087, longitude: -77.035 },
  'Los Olivos': { latitude: -11.981, longitude: -77.067 },
  Lurigancho: { latitude: -11.980, longitude: -76.840 },
  Lurin: { latitude: -12.275, longitude: -76.872 },
  'Magdalena del Mar': { latitude: -12.090, longitude: -77.069 },
  Miraflores: { latitude: -12.120, longitude: -77.030 },
  Pachacamac: { latitude: -12.232, longitude: -76.870 },
  'Pueblo Libre': { latitude: -12.078, longitude: -77.073 },
  'Puente Piedra': { latitude: -11.872, longitude: -77.069 },
  Rimac: { latitude: -12.026, longitude: -77.026 },
  'San Borja': { latitude: -12.101, longitude: -77.001 },
  'San Isidro': { latitude: -12.096, longitude: -77.036 },
  'San Juan de Lurigancho': { latitude: -12.006, longitude: -76.988 },
  'San Juan de Miraflores': { latitude: -12.152, longitude: -76.963 },
  'San Luis': { latitude: -12.073, longitude: -77.000 },
  'San Martin de Porres': { latitude: -12.015, longitude: -77.059 },
  'San Miguel': { latitude: -12.080, longitude: -77.096 },
  'Santa Anita': { latitude: -12.052, longitude: -76.971 },
  'Santa Rosa': { latitude: -11.780, longitude: -77.170 },
  'Santiago de Surco': { latitude: -12.140, longitude: -76.995 },
  Surquillo: { latitude: -12.116, longitude: -77.022 },
  'Villa El Salvador': { latitude: -12.193, longitude: -76.949 },
  'Villa Maria del Triunfo': { latitude: -12.161, longitude: -76.944 },
  'Cercado de Lima': { latitude: -12.046, longitude: -77.031 },
  Bellavista: { latitude: -12.062, longitude: -77.115 },
  Callao: { latitude: -12.055, longitude: -77.130 },
  'Carmen de la Legua': { latitude: -12.052, longitude: -77.098 },
  'La Perla': { latitude: -12.071, longitude: -77.118 },
  'La Punta': { latitude: -12.077, longitude: -77.162 },
  Ventanilla: { latitude: -11.875, longitude: -77.130 },
};

const CLEAN_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.man_made', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
];

const STATUS_COLORS = {
  pending: '#F97316',
  encamino: '#3B82F6',
  entregado: '#22C55E',
  cancelado: '#EF4444',
  rechazado: '#EF4444',
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  encamino: 'En camino',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
  rechazado: 'Rechazado',
};

function driverIdentity(driver) {
  return driver?.user_id || driver?.id || driver?.driver_profile_id;
}

function driverDisplayName(driver) {
  const name = String(driver?.name || driver?.username || '').trim();
  if (name) return name;
  const emailName = String(driver?.email || '').split('@')[0].trim();
  return emailName || 'Conductor';
}

function driverFirstName(driver) {
  return driverDisplayName(driver).split(/\s+/)[0] || 'Conductor';
}

function driverInitials(driver) {
  const words = driverDisplayName(driver)
    .replace(/[^a-zA-ZÀ-ÿ0-9\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return 'FM';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function driverLocationKey(driver, position) {
  return [
    driver?.last_location_update || '',
    position?.source || '',
    position?.lat ?? '',
    position?.lng ?? '',
  ].join('|');
}

function parseCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeDistrictName(name = '') {
  return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function getDistrictCoordinates(districtName) {
  if (!districtName) return null;
  if (DISTRICTS[districtName]) return DISTRICTS[districtName];

  const normalized = normalizeDistrictName(districtName);
  const match = Object.entries(DISTRICTS).find(([name]) => normalizeDistrictName(name) === normalized);
  return match?.[1] || null;
}

function deterministicOffset(id) {
  const value = Number(id) || 1;
  const angle = (value * 137.508) * (Math.PI / 180);
  const radius = 0.0015 + ((value % 5) * 0.00035);
  return {
    lat: Math.sin(angle) * radius,
    lng: Math.cos(angle) * radius,
  };
}

function getDeliveryPosition(point) {
  const latitude = parseCoordinate(point.latitude);
  const longitude = parseCoordinate(point.longitude);
  if (latitude && longitude) {
    return { lat: latitude, lng: longitude, estimated: false };
  }

  const district = getDistrictCoordinates(point.district);
  if (!district) return null;

  const offset = deterministicOffset(point.id);
  return {
    lat: district.latitude + offset.lat,
    lng: district.longitude + offset.lng,
    estimated: true,
  };
}

function getDriverPosition(driver) {
  const latitude = parseCoordinate(driver.latitude);
  const longitude = parseCoordinate(driver.longitude);
  if (latitude && longitude) {
    return { lat: latitude, lng: longitude, source: 'live' };
  }

  const routeStartLatitude = parseCoordinate(driver.route_start_latitude);
  const routeStartLongitude = parseCoordinate(driver.route_start_longitude);
  if (routeStartLatitude && routeStartLongitude) {
    return { lat: routeStartLatitude, lng: routeStartLongitude, source: 'route_start' };
  }

  const finalLatitude = parseCoordinate(driver.final_latitude);
  const finalLongitude = parseCoordinate(driver.final_longitude);
  if (finalLatitude && finalLongitude) {
    return { lat: finalLatitude, lng: finalLongitude, source: 'final' };
  }

  return null;
}

function highestSeverity(alerts = []) {
  if (alerts.some(alert => alert.severity === 'high')) return 'high';
  if (alerts.some(alert => alert.severity === 'medium')) return 'medium';
  if (alerts.some(alert => alert.severity === 'low')) return 'low';
  return null;
}

function severityLabel(severity) {
  if (severity === 'high') return 'critica';
  if (severity === 'medium') return 'media';
  if (severity === 'low') return 'baja';
  return '';
}

function injectMarkerStyles() {
  if (typeof document === 'undefined') return;

  // In local dev / hot reload this style tag may already exist with the older
  // delivery-only CSS. Always refresh its content so new driver overlay classes
  // are available; otherwise the DOM renders only raw text/badges like "1".
  let style = document.getElementById('fm-control-tower-marker-styles');
  const shouldAppend = !style;
  if (!style) {
    style = document.createElement('style');
  }
  style.id = 'fm-control-tower-marker-styles';
  style.textContent = `
    @keyframes fm-ct-pulse {
      0% { transform: translate(-50%, -50%) scale(.62); opacity: .65; }
      70% { transform: translate(-50%, -50%) scale(1.75); opacity: 0; }
      100% { transform: translate(-50%, -50%) scale(1.75); opacity: 0; }
    }
    @keyframes fm-ct-float {
      0%, 100% { transform: translate(-50%, -100%) translateY(0); }
      50% { transform: translate(-50%, -100%) translateY(-5px); }
    }
    @keyframes fm-driver-radar {
      0% { transform: translate(-50%, -50%) scale(.55); opacity: .5; }
      75% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
      100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
    }
    @keyframes fm-driver-core {
      0%, 100% { transform: translate(-50%, -50%) translateY(0) scale(1); }
      50% { transform: translate(-50%, -50%) translateY(-3px) scale(1.035); }
    }
    @keyframes fm-driver-shimmer {
      0% { transform: translateX(-140%) rotate(18deg); opacity: 0; }
      35% { opacity: .55; }
      100% { transform: translateX(150%) rotate(18deg); opacity: 0; }
    }
    @keyframes fm-driver-bubble {
      0% { opacity: 0; transform: translate(-50%, -112%) translateY(7px) scale(.96); }
      12% { opacity: 1; transform: translate(-50%, -112%) translateY(0) scale(1); }
      78% { opacity: 1; transform: translate(-50%, -112%) translateY(0) scale(1); }
      100% { opacity: 0; transform: translate(-50%, -112%) translateY(-6px) scale(.98); }
    }
    .fm-ct-marker { position: absolute; cursor: pointer; transform: translate(-50%, -100%); touch-action: manipulation; user-select: none; }
    .fm-ct-marker.has-alert { animation: fm-ct-float 1.8s ease-in-out infinite; }
    .fm-ct-pin-wrap { position: relative; width: 42px; height: 48px; display: flex; align-items: flex-start; justify-content: center; }
    .fm-ct-pulse { position: absolute; top: 19px; left: 50%; width: 40px; height: 40px; border-radius: 9999px; transform: translate(-50%, -50%); animation: fm-ct-pulse 1.65s ease-out infinite; }
    .fm-ct-pulse.high { background: rgba(239, 68, 68, .32); border: 2px solid rgba(239, 68, 68, .45); }
    .fm-ct-pulse.medium { background: rgba(245, 158, 11, .32); border: 2px solid rgba(245, 158, 11, .45); }
    .fm-ct-pulse.low { background: rgba(100, 116, 139, .25); border: 2px solid rgba(100, 116, 139, .35); }
    .fm-ct-pin { width: 34px; height: 34px; border-radius: 9999px; border: 3px solid #fff; box-shadow: 0 12px 24px rgba(15, 23, 42, .28); display: grid; place-items: center; color: #fff; font: 900 13px/1 Montserrat, sans-serif; position: relative; transition: transform .18s ease, box-shadow .18s ease; }
    .fm-ct-pin::after { content: ''; position: absolute; left: 50%; bottom: -9px; transform: translateX(-50%); width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent; border-top: 10px solid var(--pin-color); filter: drop-shadow(0 5px 4px rgba(15,23,42,.22)); }
    .fm-ct-marker:hover .fm-ct-pin, .fm-ct-marker.selected .fm-ct-pin { transform: scale(1.12) translateY(-2px); box-shadow: 0 16px 30px rgba(15, 23, 42, .36); }
    .fm-ct-marker.selected .fm-ct-pin { outline: 3px solid rgba(177, 51, 110, .28); }
    .fm-ct-badge { position: absolute; top: -7px; right: -8px; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 9999px; background: #dc2626; color: #fff; border: 2px solid #fff; display: flex; align-items: center; justify-content: center; font: 900 10px/1 Montserrat, sans-serif; box-shadow: 0 5px 12px rgba(220, 38, 38, .35); }
    .fm-ct-estimated { position: absolute; left: -5px; bottom: 5px; width: 16px; height: 16px; border-radius: 9999px; background: #fff; color: #64748b; border: 1px solid #cbd5e1; display: grid; place-items: center; font: 900 10px/1 Montserrat, sans-serif; }
    .fm-driver-marker { position: absolute; width: 72px; height: 76px; transform: translate(-50%, -69px); cursor: pointer; touch-action: manipulation; user-select: none; outline: none; will-change: left, top; }
    .fm-driver-field { position: absolute; inset: 0; pointer-events: none; }
    .fm-driver-radar { position: absolute; left: 50%; top: 36px; width: 44px; height: 44px; border-radius: 9999px; transform: translate(-50%, -50%); animation: fm-driver-radar 2.2s ease-out infinite; }
    .fm-driver-radar.two { animation-delay: .85s; }
    .fm-driver-marker.is-live .fm-driver-radar { background: rgba(14, 165, 233, .20); border: 2px solid rgba(14, 165, 233, .45); }
    .fm-driver-marker.is-reference .fm-driver-radar { background: rgba(148, 163, 184, .18); border: 2px dashed rgba(100, 116, 139, .38); animation-duration: 3s; }
    .fm-driver-marker.has-driver-alert .fm-driver-radar { background: rgba(239, 68, 68, .18); border-color: rgba(239, 68, 68, .48); }
    .fm-driver-core { position: absolute; left: 50%; top: 36px; width: 44px; height: 44px; transform: translate(-50%, -50%); border-radius: 9999px; border: 3px solid #fff; display: grid; place-items: center; overflow: hidden; color: #fff; font: 950 14px/1 Montserrat, sans-serif; letter-spacing: -.03em; box-shadow: 0 16px 30px rgba(15, 23, 42, .34); animation: fm-driver-core 1.9s ease-in-out infinite; transition: transform .18s ease, box-shadow .18s ease; }
    .fm-driver-marker.is-live .fm-driver-core { background: linear-gradient(135deg, #0ea5e9, #2563eb 58%, #7c3aed); }
    .fm-driver-marker.is-reference .fm-driver-core { background: linear-gradient(135deg, #64748b, #334155); opacity: .9; }
    .fm-driver-marker:hover .fm-driver-core, .fm-driver-marker.is-active .fm-driver-core { animation: none; transform: translate(-50%, -50%) scale(1.12); box-shadow: 0 20px 38px rgba(37, 99, 235, .42); }
    .fm-driver-marker.is-active .fm-driver-core { outline: 4px solid rgba(14, 165, 233, .24); }
    .fm-driver-core::after { content: ''; position: absolute; inset: -20% auto -20% -35%; width: 24px; background: rgba(255,255,255,.72); filter: blur(6px); animation: fm-driver-shimmer 3.4s ease-in-out infinite; }
    .fm-driver-initials { position: relative; z-index: 1; text-shadow: 0 1px 2px rgba(0,0,0,.22); }
    .fm-driver-pointer { position: absolute; left: 50%; top: 58px; transform: translateX(-50%); width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 11px solid #2563eb; filter: drop-shadow(0 6px 4px rgba(15,23,42,.22)); }
    .fm-driver-marker.is-reference .fm-driver-pointer { border-top-color: #334155; }
    .fm-driver-name { position: absolute; left: 50%; top: 65px; transform: translateX(-50%); max-width: 118px; padding: 4px 8px; border-radius: 9999px; background: rgba(255,255,255,.96); color: #0f172a; border: 1px solid rgba(226,232,240,.95); box-shadow: 0 8px 18px rgba(15,23,42,.14); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font: 800 11px/1.1 Montserrat, sans-serif; }
    .fm-driver-mini { position: absolute; right: 4px; top: 9px; min-width: 22px; height: 22px; padding: 0 6px; border-radius: 9999px; background: #0f172a; color: #fff; border: 2px solid #fff; display: flex; align-items: center; justify-content: center; font: 900 10px/1 Montserrat, sans-serif; box-shadow: 0 8px 16px rgba(15,23,42,.24); }
    .fm-driver-alert { position: absolute; left: 5px; top: 12px; width: 20px; height: 20px; border-radius: 9999px; background: #dc2626; color: #fff; border: 2px solid #fff; display: grid; place-items: center; font: 900 11px/1 Montserrat, sans-serif; box-shadow: 0 8px 16px rgba(220,38,38,.36); }
    .fm-driver-bubble { position: absolute; left: 50%; bottom: 72px; transform: translate(-50%, -112%); min-width: 148px; max-width: 220px; padding: 9px 11px; border-radius: 15px; background: #0f172a; color: #fff; box-shadow: 0 16px 34px rgba(15,23,42,.32); font: 800 11px/1.25 Montserrat, sans-serif; text-align: center; pointer-events: none; animation: fm-driver-bubble 5.8s ease forwards; }
    .fm-driver-bubble::after { content: ''; position: absolute; left: 50%; bottom: -7px; transform: translateX(-50%); width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent; border-top: 8px solid #0f172a; }
    .fm-driver-bubble-sub { display: block; margin-top: 2px; color: #bfdbfe; font: 700 10px/1.2 Montserrat, sans-serif; }
    .fm-driver-marker.is-reference .fm-driver-bubble { background: #334155; }
    .fm-driver-marker.is-reference .fm-driver-bubble::after { border-top-color: #334155; }
    @media (max-width: 640px) {
      .fm-driver-marker { width: 62px; height: 66px; }
      .fm-driver-core { width: 40px; height: 40px; }
      .fm-driver-name { display: none; }
      .fm-driver-marker.is-active .fm-driver-name { display: block; max-width: 96px; font-size: 10px; top: 62px; }
      .fm-driver-bubble { min-width: 132px; max-width: 170px; bottom: 66px; font-size: 10px; padding: 8px 10px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fm-ct-marker.has-alert, .fm-ct-pulse, .fm-driver-radar, .fm-driver-core, .fm-driver-core::after, .fm-driver-bubble { animation: none !important; }
    }
  `;
  if (shouldAppend) {
    document.head.appendChild(style);
  }
}

function createDeliveryOverlay({ google, map, position, delivery, alerts, selected, onClick }) {
  const overlay = new google.maps.OverlayView();
  const severity = highestSeverity(alerts);
  const color = STATUS_COLORS[delivery.status] || '#64748B';
  let root;
  let clickHandler;
  let keyHandler;

  overlay.onAdd = function onAdd() {
    root = document.createElement('div');
    root.className = `fm-ct-marker ${alerts.length > 0 ? 'has-alert' : ''} ${selected ? 'selected' : ''}`;
    root.title = `Entrega #${delivery.id} - ${delivery.recipient_name || ''}${alerts.length ? ` - ${alerts.length} alerta(s)` : ''}`;
    root.tabIndex = 0;
    root.setAttribute('role', 'button');
    root.setAttribute('aria-label', root.title);

    const wrap = document.createElement('div');
    wrap.className = 'fm-ct-pin-wrap';

    if (alerts.length > 0) {
      const pulse = document.createElement('div');
      pulse.className = `fm-ct-pulse ${severity || 'medium'}`;
      wrap.appendChild(pulse);
    }

    const pin = document.createElement('div');
    pin.className = 'fm-ct-pin';
    pin.style.background = color;
    pin.style.setProperty('--pin-color', color);
    pin.textContent = delivery.status === 'encamino' ? '>' : delivery.status === 'entregado' ? 'OK' : delivery.status === 'pending' ? '!' : '.';
    wrap.appendChild(pin);

    if (alerts.length > 0) {
      const badge = document.createElement('div');
      badge.className = 'fm-ct-badge';
      badge.textContent = String(alerts.length);
      wrap.appendChild(badge);
    }

    if (position.estimated) {
      const estimated = document.createElement('div');
      estimated.className = 'fm-ct-estimated';
      estimated.textContent = '~';
      estimated.title = 'Ubicacion estimada por distrito';
      wrap.appendChild(estimated);
    }

    root.appendChild(wrap);
    clickHandler = event => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    };
    keyHandler = event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    };
    root.addEventListener('click', clickHandler);
    root.addEventListener('keydown', keyHandler);

    this.getPanes().overlayMouseTarget.appendChild(root);
  };

  overlay.draw = function draw() {
    const projection = this.getProjection();
    if (!projection || !root) return;
    const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(position.lat, position.lng));
    root.style.left = `${point.x}px`;
    root.style.top = `${point.y}px`;
    root.style.zIndex = String(alerts.length ? 1000 : selected ? 900 : 500);
  };

  overlay.onRemove = function onRemove() {
    if (!root) return;
    root.removeEventListener('click', clickHandler);
    root.removeEventListener('keydown', keyHandler);
    root.remove();
    root = null;
  };

  overlay.setMap(map);
  return overlay;
}

function createDriverOverlay({ google, map, position, driver, workload, selected, showBubble, onClick }) {
  const overlay = new google.maps.OverlayView();
  const name = driverDisplayName(driver);
  const firstName = driverFirstName(driver);
  const isLive = position.source === 'live';
  const hasAlerts = (workload.alertCount || 0) > 0;
  const stops = (workload.deliveryCount || 0) + (workload.pickupCount || 0);
  const bubbleMessage = !isLive
    ? `Ubicacion referencial de ${firstName}`
    : workload.enCaminoCount > 0
      ? `${firstName} esta de camino`
      : `${firstName} esta en ruta`;
  const bubbleSubtext = stops > 0
    ? `${stops} parada${stops !== 1 ? 's' : ''} activa${stops !== 1 ? 's' : ''}`
    : 'GPS activo';

  let root;
  let clickHandler;
  let keyHandler;

  overlay.onAdd = function onAdd() {
    root = document.createElement('div');
    root.className = [
      'fm-driver-marker',
      isLive ? 'is-live' : 'is-reference',
      selected ? 'is-active' : '',
      hasAlerts ? 'has-driver-alert' : '',
    ].filter(Boolean).join(' ');
    root.tabIndex = 0;
    root.setAttribute('role', 'button');
    root.title = `${name}${isLive ? ' - GPS en vivo' : ' - ubicacion referencial'}${stops ? ` - ${stops} parada(s)` : ''}`;
    root.setAttribute('aria-label', root.title);

    const field = document.createElement('div');
    field.className = 'fm-driver-field';

    const radarOne = document.createElement('div');
    radarOne.className = 'fm-driver-radar one';
    field.appendChild(radarOne);

    const radarTwo = document.createElement('div');
    radarTwo.className = 'fm-driver-radar two';
    field.appendChild(radarTwo);

    const core = document.createElement('div');
    core.className = 'fm-driver-core';

    const initials = document.createElement('span');
    initials.className = 'fm-driver-initials';
    initials.textContent = driverInitials(driver);
    core.appendChild(initials);

    const pointer = document.createElement('div');
    pointer.className = 'fm-driver-pointer';

    const nameLabel = document.createElement('div');
    nameLabel.className = 'fm-driver-name';
    nameLabel.textContent = name;

    if (stops > 0) {
      const mini = document.createElement('div');
      mini.className = 'fm-driver-mini';
      mini.textContent = String(stops);
      mini.title = `${stops} parada(s) activa(s)`;
      field.appendChild(mini);
    }

    if (hasAlerts) {
      const alert = document.createElement('div');
      alert.className = 'fm-driver-alert';
      alert.textContent = '!';
      alert.title = `${workload.alertCount} alerta(s) en entregas asignadas`;
      field.appendChild(alert);
    }

    root.appendChild(field);
    root.appendChild(core);
    root.appendChild(pointer);
    root.appendChild(nameLabel);

    if (showBubble) {
      const bubble = document.createElement('div');
      bubble.className = 'fm-driver-bubble';
      bubble.textContent = bubbleMessage;

      const sub = document.createElement('span');
      sub.className = 'fm-driver-bubble-sub';
      sub.textContent = bubbleSubtext;
      bubble.appendChild(sub);

      root.appendChild(bubble);
    }

    clickHandler = event => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    };
    keyHandler = event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    };
    root.addEventListener('click', clickHandler);
    root.addEventListener('keydown', keyHandler);

    this.getPanes().overlayMouseTarget.appendChild(root);
  };

  overlay.draw = function draw() {
    const projection = this.getProjection();
    if (!projection || !root) return;
    const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(position.lat, position.lng));
    root.style.left = `${point.x}px`;
    root.style.top = `${point.y}px`;
    root.style.zIndex = String(hasAlerts ? 2300 : selected ? 2250 : 2100);
  };

  overlay.onRemove = function onRemove() {
    if (!root) return;
    root.removeEventListener('click', clickHandler);
    root.removeEventListener('keydown', keyHandler);
    root.remove();
    root = null;
  };

  overlay.setMap(map);
  return overlay;
}

function SelectedDeliveryPanel({ delivery, alerts, position, onClose, onOpenDetails, onFocus }) {
  if (!delivery) return null;

  const severity = highestSeverity(alerts);
  const primaryAlert = alerts[0];

  return (
    <div className="absolute left-3 right-3 bottom-3 lg:left-4 lg:right-auto lg:w-[24rem] rounded-2xl bg-white shadow-2xl border border-slate-200 z-20 overflow-hidden">
      <div className={`h-1 ${severity === 'high' ? 'bg-red-500' : severity ? 'bg-amber-400' : 'bg-brand-primary'}`} />
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-black text-slate-400 font-montserrat">#{delivery.id}</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-montserrat">
                {STATUS_LABELS[delivery.status] || delivery.status}
              </span>
              {position?.estimated && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-montserrat">
                  ubicacion estimada
                </span>
              )}
            </div>
            <h3 className="mt-1 text-sm font-extrabold text-slate-900 font-montserrat truncate">
              {delivery.recipient_name || 'Entrega'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Cerrar seleccion">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {alerts.length > 0 && (
          <div className={`rounded-xl border px-3 py-2 ${severity === 'high' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
            <div className="flex items-center gap-2">
              <AlertTriangle className={`w-4 h-4 shrink-0 ${severity === 'high' ? 'animate-pulse' : ''}`} />
              <p className="text-xs font-extrabold font-montserrat truncate">
                {alerts.length} alerta{alerts.length !== 1 ? 's' : ''} - {severityLabel(severity)}
              </p>
            </div>
            <p className="mt-1 text-xs font-semibold font-montserrat truncate">
              {getAlertLabel(primaryAlert?.alert_type)}{primaryAlert?.message ? ` - ${primaryAlert.message}` : ''}
            </p>
          </div>
        )}

        <div className="flex items-start gap-2 text-xs text-slate-600 font-montserrat">
          <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
          <span className="line-clamp-2">{delivery.address}, {delivery.district}</span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button onClick={onFocus} className="rounded-xl border border-slate-200 px-2 py-2 text-xs font-bold text-slate-700 font-montserrat hover:bg-slate-50 flex items-center justify-center gap-1">
            <Crosshair className="w-3.5 h-3.5" />
            Centrar
          </button>
          {delivery.recipient_phone ? (
            <a href={`tel:${delivery.recipient_phone}`} className="rounded-xl border border-slate-200 px-2 py-2 text-xs font-bold text-slate-700 font-montserrat hover:bg-slate-50 flex items-center justify-center gap-1">
              <Phone className="w-3.5 h-3.5" />
              Llamar
            </a>
          ) : (
            <span className="rounded-xl border border-slate-100 px-2 py-2 text-xs font-bold text-slate-300 font-montserrat flex items-center justify-center">Sin tel.</span>
          )}
          <button onClick={onOpenDetails} className="rounded-xl bg-brand-primary px-2 py-2 text-xs font-bold text-white font-montserrat hover:bg-brand-primary/90">
            Detalle
          </button>
        </div>
      </div>
    </div>
  );
}

function ControlTower({ userId, setActiveComponent }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const deliveryMarkersRef = useRef({});
  const driverMarkersRef = useRef({});
  const driverLocationKeysRef = useRef({});
  const orderMarkersRef = useRef({});
  const didFitBoundsRef = useRef(false);
  const [detailDelivery, setDetailDelivery] = useState(null);
  const [intelligenceDelivery, setIntelligenceDelivery] = useState(null);

  const tower = useControlTower(userId);
  const setSelectedDelivery = tower.setSelectedDelivery;
  const setShowList = tower.setShowList;

  const selectedDeliveryPosition = useMemo(() => {
    if (tower.selectedDelivery?.type !== 'regular') return null;
    return getDeliveryPosition(tower.selectedDelivery.data);
  }, [tower.selectedDelivery]);

  const driverWorkloadById = useMemo(() => {
    const workload = {};

    const ensure = driverId => {
      if (!driverId) return null;
      const key = String(driverId);
      if (!workload[key]) {
        workload[key] = {
          deliveryCount: 0,
          pickupCount: 0,
          enCaminoCount: 0,
          alertCount: 0,
          firstDelivery: null,
        };
      }
      return workload[key];
    };

    tower.deliveryPoints.forEach(point => {
      const item = ensure(point.driver_id);
      if (!item) return;
      item.deliveryCount += 1;
      if (point.status === 'encamino') item.enCaminoCount += 1;
      const alerts = tower.alertsByDeliveryId[point.id] || [];
      item.alertCount += alerts.length;
      if (!item.firstDelivery || alerts.length > 0 || point.status === 'encamino') {
        item.firstDelivery = point;
      }
    });

    tower.orders.forEach(order => {
      const item = ensure(order.driver_id);
      if (item) item.pickupCount += 1;
    });

    return workload;
  }, [tower.alertsByDeliveryId, tower.deliveryPoints, tower.orders]);

  const panToDelivery = useCallback((point, zoom = 16) => {
    const position = getDeliveryPosition(point);
    if (!position || !mapRef.current) return;
    mapRef.current.panTo({ lat: position.lat, lng: position.lng });
    mapRef.current.setZoom(zoom);
  }, []);

  const focusDelivery = useCallback((point, options = {}) => {
    setSelectedDelivery({ type: 'regular', data: point });
    panToDelivery(point);

    if (options.openListOnMobile !== true && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setShowList(false);
    }
  }, [panToDelivery, setSelectedDelivery, setShowList]);

  const focusDriver = useCallback((position, workload = {}) => {
    if (!position || !mapRef.current) return;
    mapRef.current.panTo({ lat: position.lat, lng: position.lng });
    mapRef.current.setZoom(Math.max(mapRef.current.getZoom() || 13, 15));

    if (workload.firstDelivery) {
      setSelectedDelivery({ type: 'regular', data: workload.firstDelivery });
      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setShowList(false);
      }
    }
  }, [setSelectedDelivery, setShowList]);

  useEffect(() => {
    injectMarkerStyles();
  }, []);

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current && window.google) {
      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: -12.0602, lng: -77.0371 },
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'greedy',
        styles: CLEAN_MAP_STYLES,
        backgroundColor: '#f8fafc',
      });
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    Object.values(deliveryMarkersRef.current).forEach(marker => marker.setMap(null));
    deliveryMarkersRef.current = {};

    tower.deliveryPoints.forEach(point => {
      const position = getDeliveryPosition(point);
      if (!position) return;

      const alerts = tower.alertsByDeliveryId[point.id] || [];
      const isSelected = tower.selectedDelivery?.type === 'regular' && tower.selectedDelivery.data?.id === point.id;

      const marker = createDeliveryOverlay({
        google: window.google,
        map: mapRef.current,
        position,
        delivery: point,
        alerts,
        selected: isSelected,
        onClick: () => focusDelivery(point, { openListOnMobile: false }),
      });

      deliveryMarkersRef.current[point.id] = marker;
    });
  }, [tower.deliveryPoints, tower.alertsByDeliveryId, tower.selectedDelivery, focusDelivery]);

  useEffect(() => {
    if (!mapRef.current || !window.google || didFitBoundsRef.current || tower.deliveryPoints.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoint = false;

    tower.deliveryPoints.forEach(point => {
      const position = getDeliveryPosition(point);
      if (!position) return;
      bounds.extend({ lat: position.lat, lng: position.lng });
      hasPoint = true;
    });

    if (hasPoint) {
      mapRef.current.fitBounds(bounds, window.innerWidth < 768 ? 48 : 80);
      didFitBoundsRef.current = true;
    }
  }, [tower.deliveryPoints]);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    Object.values(orderMarkersRef.current).forEach(marker => marker.setMap(null));
    orderMarkersRef.current = {};

    tower.orders.forEach(order => {
      const addr = tower.user.addresses?.find(a => a.id === order.address_id);
      if (!addr?.latitude || !addr?.longitude) return;

      const marker = new window.google.maps.Marker({
        position: { lat: Number(addr.latitude), lng: Number(addr.longitude) },
        map: mapRef.current,
        icon: {
          url: `${process.env.PUBLIC_URL}/icons/store.svg`,
          scaledSize: new window.google.maps.Size(30, 30),
        },
        title: 'Punto de recojo / tienda',
        zIndex: 120,
      });
      orderMarkersRef.current[order.id] = marker;
    });
  }, [tower.orders, tower.user.addresses]);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    Object.values(driverMarkersRef.current).forEach(marker => marker.setMap(null));
    driverMarkersRef.current = {};
    const activeDriverKeys = new Set();

    tower.drivers.forEach(driver => {
      const position = getDriverPosition(driver);
      if (!position) return;

      const identity = driverIdentity(driver);
      if (!identity) return;

      const driverKey = String(identity);
      activeDriverKeys.add(driverKey);

      const workload = driverWorkloadById[driverKey] || {};
      const isSelectedDriver = (
        tower.selectedDelivery?.type === 'regular'
        && String(tower.selectedDelivery.data?.driver_id) === driverKey
      );
      const locationKey = driverLocationKey(driver, position);
      const showBubble = driverLocationKeysRef.current[driverKey] !== locationKey;
      driverLocationKeysRef.current[driverKey] = locationKey;

      const marker = createDriverOverlay({
        google: window.google,
        map: mapRef.current,
        position,
        driver,
        workload,
        selected: isSelectedDriver,
        showBubble,
        onClick: () => focusDriver(position, workload),
      });

      driverMarkersRef.current[driverKey] = marker;
    });

    Object.keys(driverLocationKeysRef.current).forEach(driverKey => {
      if (!activeDriverKeys.has(driverKey)) {
        delete driverLocationKeysRef.current[driverKey];
      }
    });
  }, [tower.drivers, tower.selectedDelivery, driverWorkloadById, focusDriver]);

  function handleDeliveryClick(item) {
    if (item.type !== 'regular') return;
    focusDelivery(item.data);
  }

  function openAlertsList() {
    tower.setListFilter('alerts');
    tower.setShowList(true);
  }

  const selectedAlerts = tower.selectedDelivery?.type === 'regular'
    ? tower.alertsByDeliveryId[tower.selectedDelivery.data?.id] || []
    : [];

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
      <TowerHeader
        setActiveComponent={setActiveComponent}
        showList={tower.showList}
        onToggleList={() => tower.setShowList(s => !s)}
        deliveryPoints={tower.deliveryPoints}
        alertSummary={tower.alertSummary}
      />

      <div className="flex-1 flex overflow-hidden relative">
        <TowerSidebar
          showList={tower.showList}
          searchQuery={tower.searchQuery}
          onSearchChange={tower.setSearchQuery}
          filteredRegular={tower.filteredRegular}
          totalRegular={tower.deliveryPoints.length}
          selectedDelivery={tower.selectedDelivery}
          onDeliveryClick={handleDeliveryClick}
          alertsByDeliveryId={tower.alertsByDeliveryId}
          alertSummary={tower.alertSummary}
          intelligenceByDeliveryId={tower.intelligenceByDeliveryId}
          onIntelligenceClick={setIntelligenceDelivery}
          listFilter={tower.listFilter}
          setListFilter={tower.setListFilter}
        />

        <div className="flex-1 relative bg-slate-100">
          <div ref={mapContainerRef} className="w-full h-full" />

          <div className="absolute top-3 left-3 right-3 lg:left-4 lg:right-4 z-10 pointer-events-none flex items-start justify-between gap-2">
            <div className="flex flex-col gap-2 pointer-events-auto">
              {tower.alertSummary.total > 0 && (
                <button
                  onClick={openAlertsList}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 shadow-xl border text-xs font-extrabold font-montserrat ${tower.alertSummary.high > 0 ? 'bg-red-600 text-white border-red-500' : 'bg-amber-100 text-amber-800 border-amber-200'}`}
                >
                  <AlertTriangle className={`w-4 h-4 ${tower.alertSummary.high > 0 ? 'animate-pulse' : ''}`} />
                  {tower.alertSummary.total} alerta{tower.alertSummary.total !== 1 ? 's' : ''} activas
                </button>
              )}

              <div className="hidden sm:flex items-center gap-2 rounded-2xl bg-white/95 backdrop-blur border border-slate-200 shadow-xl px-3 py-2 text-[11px] font-semibold text-slate-600 font-montserrat">
                <Layers className="w-4 h-4 text-slate-400" />
                <span><b className="text-orange-600">*</b> pendiente</span>
                <span><b className="text-blue-600">*</b> en camino</span>
                <span><b className="text-red-600">o</b> alerta</span>
                <span><b className="text-sky-600">FM</b> conductor</span>
                <span><b className="text-slate-500">~</b> estimada</span>
              </div>
            </div>

            <div className="hidden md:flex max-w-xs items-start gap-2 rounded-2xl bg-white/90 backdrop-blur border border-slate-200 shadow-xl px-3 py-2 text-[11px] text-slate-600 font-montserrat pointer-events-auto">
              <Info className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
              <span>Mapa limpio: ocultamos museos, malls, paraderos y POIs para priorizar entregas, tiendas y conductores.</span>
            </div>
          </div>

          {tower.loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
              <div className="w-10 h-10 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          <SelectedDeliveryPanel
            delivery={tower.selectedDelivery?.data}
            alerts={selectedAlerts}
            position={selectedDeliveryPosition}
            onClose={() => tower.setSelectedDelivery(null)}
            onFocus={() => tower.selectedDelivery?.data && panToDelivery(tower.selectedDelivery.data)}
            onOpenDetails={() => setDetailDelivery(tower.selectedDelivery)}
          />
        </div>
      </div>

      <StatusFooter deliveryPoints={tower.deliveryPoints} alertSummary={tower.alertSummary} />

      {detailDelivery?.type === 'regular' && (
        <RegularDeliveryModal
          delivery={detailDelivery.data}
          onClose={() => setDetailDelivery(null)}
        />
      )}
      {intelligenceDelivery && (
        <StoreDeliveryIntelligenceDrawer
          item={intelligenceDelivery}
          onClose={() => setIntelligenceDelivery(null)}
        />
      )}
    </div>
  );
}

export default ControlTower;
